import React, { useEffect, useState } from "react";
import "./EmailVerify.css";
import { Flex, Button, Input, message, Form } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";
import {
  forgetpassword,
  otpVerify,
  verifyFactorOtp,
  getMyPermissions,
} from "../../services/ApiService";
import { decryptObject, encryptObject } from "../GlobalFunction";
import { setupPushNotifications } from "../../services/pushNotifications";

const EmailVerify = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { text = "" } = location.state || {};
  const [timer, setTimer] = useState(60);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [isOtpComplete, setIsOtpComplete] = useState(false);
  const email =
    text === "Email Verification"
      ? sessionStorage.getItem("email")
      : sessionStorage.getItem("emailId");

  useEffect(() => {
    if (timer === 0) return;
    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const handleOtpChange = (value) => {
    const enteredOtp = value.join("");
    setOtp(enteredOtp);
    setIsOtpComplete(enteredOtp.length === 6);
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      const response = await forgetpassword({ email });
      message[response.responseCode === 200 ? "success" : "error"](
        response.responseMessage
      );
      setTimer(60);
    } catch (err) {
      message.error("Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);

    try {
      let response;

      if (text === "Email Verification") {
        response = await otpVerify({ email, otp });
      } else {
        response = await verifyFactorOtp({ email, otp });
      }

      if (response.responseCode === 200) {
        message.success(response.responseMessage);

        if (text === "Email Verification") {
          navigate("/reset-password");
        } else {
          sessionStorage.setItem("isVerified", true);
          const encyptData = encryptObject(response?.data);
          sessionStorage.setItem("sessioninfo", encyptData);

          // Fetch once at login and cache for the session -- Sidebar (and,
          // going forward, page-level action gating) reads this instead of
          // the old hardcoded userType checks. Non-fatal if it fails: the
          // old userType-based checks still work as a fallback everywhere
          // this isn't wired in yet.
          try {
            const permRes = await getMyPermissions();
            if (permRes?.responseCode === 200) {
              sessionStorage.setItem(
                "permissions",
                JSON.stringify(permRes.data.permissions)
              );
            }
          } catch (err) {
            console.error("Failed to fetch permissions", err);
          }

          // Fire-and-forget -- the permission prompt/subscribe flow
          // should never hold up login/navigation. setupPushNotifications
          // catches its own errors internally, so nothing here can throw.
          if (response?.data?.employee_id) {
            setupPushNotifications(response.data.employee_id);
          }

          if (response?.data?.isNew) {
            navigate("/changepassword");
          } else if (response?.data?.userType === 4) {
            navigate("/dashboard-f");
          } else {
            navigate("/dashboard");
          }
        }
      } else {
        message.error(response.responseMessage);
      }
    } catch (error) {
      message.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="main-container">
        <div>
          <Form
            onFinish={handleVerify}
            layout="vertical"
            initialValues={{ otp }}
          >
            <div className="login">
              <h4 className="title">{text}</h4>
              <div className="desc">
                A 6-digit code has been sent to your email address. Enter the
                code to verify your Email.
              </div>
              <div>
                <Flex gap="middle" align="flex-start" vertical>
                  <Form.Item
                    name="otp"
                    label="Enter Code"
                    rules={[{ required: true, message: "OTP is required" }]}
                  >
                    <Input.OTP
                      maxLength={6}
                      value={otp}
                      onInput={handleOtpChange}
                    />
                  </Form.Item>
                </Flex>
                {timer > 0 && (
                  <span
                    style={{
                      marginTop: "2rem",
                      color: "red",
                      fontSize: "0.8rem",
                    }}
                  >
                    00:{timer}
                  </span>
                )}
              </div>
            </div>
            <Flex
              justify={`${timer === 0 ? "space-between" : "flex-end"}`}
              className="login-btn"
            >
              {timer === 0 && (
                <span style={{ fontSize: "0.7rem" }}>
                  Didn't receive a code?
                  <span
                    style={{
                      color: "Blue",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                    onClick={handleResend}
                  >
                    {" "}
                    Resend OTP
                  </span>
                </span>
              )}
              <Button
                type="primary"
                shape="round"
                htmlType="submit"
                style={{ backgroundColor: "#4A3AFF", padding: "1.2rem" }}
                disabled={!isOtpComplete || loading}
              >
                Verify Account
                {loading && <LoadingOutlined style={{ color: "white" }} />}
              </Button>
            </Flex>
          </Form>
        </div>
        <div className="copyright">
          Copyright © 2026 Procloz Services Pvt Ltd | All Rights Reserved
        </div>
      </div>
    </>
  );
};

export default EmailVerify;
